import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, ShoppingCart, CheckCircle, XCircle, Camera, CreditCard, Package } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import QrScanner from "./QrScanner";

export default function MerchantRedeem({ companies }) {
  const [companyId, setCompanyId] = useState("");
  const [code, setCode] = useState("");
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState("");
  const [value, setValue] = useState("");
  const [cb, setCb] = useState("");
  const [installments, setInstallments] = useState("1"); // à vista (1) ou parcelado
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);

  const refresh = () => api.get("/redemptions/mine").then((r) => setHistory(r.data));
  useEffect(() => { refresh(); }, []);

  const find = async (codeArg) => {
    const c = codeArg || code;
    if (!c) return;
    setBusy(true); setCustomer(null);
    try {
      const { data } = await api.post("/redemptions/find-customer", { wallet_code: c });
      setCustomer(data);
      toast.success(`${data.name} · saldo R$ ${data.balance.toFixed(2)}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onScan = (text) => {
    setScanOpen(false);
    setCode(text);
    find(text);
  };

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/redemptions", {
        company_id: companyId,
        wallet_code: code,
        items_description: items,
        items_value: parseFloat(value),
        cashback_to_use: parseFloat(cb),
        installments: parseInt(installments) || 1,
      });
      toast.success("Solicitação enviada! Aguardando aprovação do cliente.");
      setItems(""); setValue(""); setCb(""); setCode(""); setCustomer(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const validCompanies = companies.filter((c) => c.plan === "PRATA" || c.plan === "OURO");

  return (
    <div className="space-y-5">
      <Card className="p-6 rounded-2xl border-stone-200">
        <h3 className="font-display text-xl font-semibold flex items-center gap-2"><ShoppingCart size={20}/> Venda Direta</h3>
        <p className="text-sm text-stone-500 mt-1">Registre vendas presenciais. O cliente pode usar saldo BemCash como desconto; o complemento é cobrado fora da plataforma (PIX/cartão).</p>

        <Tabs defaultValue="maquininha" className="mt-5">
          <TabsList className="rounded-xl">
            <TabsTrigger value="maquininha" data-testid="vd-tab-maquininha"><CreditCard size={14} className="mr-1"/>Maquininha BemCash</TabsTrigger>
            <TabsTrigger value="checkout" data-testid="vd-tab-checkout"><Package size={14} className="mr-1"/>Checkout (produtos)</TabsTrigger>
          </TabsList>

          <TabsContent value="maquininha" className="mt-5">
          <form onSubmit={request} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Empresa</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rd-company"><SelectValue placeholder="Selecione"/></SelectTrigger>
                <SelectContent>
                  {validCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.plan})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Código da carteira do cliente</Label>
              <div className="flex gap-2 mt-1">
                <Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="MB-XXXX-XXXX"
                  className="rounded-xl font-mono tracking-wider uppercase" data-testid="rd-code"/>
                <Button type="button" onClick={() => setScanOpen(true)} variant="outline" className="rounded-xl shrink-0" data-testid="rd-scan-btn" title="Escanear QR">
                  <Camera size={14}/>
                </Button>
                <Button type="button" onClick={() => find()} disabled={!code || busy} variant="outline" className="rounded-xl shrink-0">
                  <Search size={14}/>
                </Button>
              </div>
            </div>
          </div>

          {customer && (
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-between" data-testid="rd-customer-info">
              <div>
                <div className="text-sm font-semibold text-blue-950">{customer.name}</div>
                <div className="text-[11px] text-stone-600 font-mono">{customer.wallet_code}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Saldo disponível</div>
                <div className="font-display text-xl font-bold text-blue-950">B$ {customer.balance.toFixed(2)}</div>
              </div>
            </div>
          )}

          <div>
            <Label>O que o cliente comprou</Label>
            <Input required value={items} onChange={(e) => setItems(e.target.value)} placeholder="2 Pizzas Calabresa, 1 Refrigerante" className="rounded-xl mt-1" data-testid="rd-items"/>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Valor total da compra (R$)</Label>
              <Input required type="number" step="0.01" min="0.01" value={value} onChange={(e) => setValue(e.target.value)} className="rounded-xl mt-1" data-testid="rd-value"/>
            </div>
            <div>
              <Label>Cashback do cliente (B$)</Label>
              <Input required type="number" step="0.01" min="0.01" value={cb} onChange={(e) => setCb(e.target.value)} className="rounded-xl mt-1" data-testid="rd-cashback"/>
              {customer && parseFloat(cb) > customer.balance && <p className="text-[11px] text-red-600 mt-1">Excede saldo do cliente</p>}
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={installments} onValueChange={setInstallments}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rd-installments"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">À vista</SelectItem>
                  {[2,3,4,5,6,7,8,9,10,11,12].map((n) => <SelectItem key={n} value={String(n)}>Parcelado {n}x</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {value && cb && parseFloat(value) > parseFloat(cb) && (
            <div className="p-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="rd-complement-info">
              Complemento a cobrar fora da plataforma: <strong>R$ {(parseFloat(value) - parseFloat(cb)).toFixed(2).replace(".", ",")}</strong> ({installments === "1" ? "à vista" : `parcelado em ${installments}x`})
            </div>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || !companyId || !customer} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="rd-submit">{busy ? "Enviando..." : "Enviar solicitação"}</Button>
          </div>
        </form>
        </TabsContent>

        <TabsContent value="checkout" className="mt-5">
          <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 text-center" data-testid="vd-checkout-mode">
            <Package size={32} className="text-stone-400 mx-auto mb-2"/>
            <h4 className="font-display text-lg font-bold text-stone-900">Venda de produtos cadastrados</h4>
            <p className="text-sm text-stone-600 mt-1 mb-4">
              Para vendas com produtos/serviços que você já cadastrou, abra o catálogo da sua empresa.
              O cliente seleciona os itens e o pagamento é processado pelo Mercado Pago.
            </p>
            {companyId ? (
              <Link to={`/loja/${companyId}`}>
                <Button className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="vd-checkout-open">
                  Abrir catálogo da loja
                </Button>
              </Link>
            ) : (
              <p className="text-xs text-stone-500">Selecione uma empresa primeiro.</p>
            )}
            <p className="text-[11px] text-stone-500 mt-3">Gerencie produtos na aba <strong>Produtos & Serviços</strong>.</p>
          </div>
        </TabsContent>
        </Tabs>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200">
        <h3 className="font-display text-lg font-semibold mb-3">Histórico de resgates ({history.length})</h3>
        <div className="divide-y divide-stone-100">
          {history.length === 0 && <p className="text-sm text-stone-500">Nenhum resgate ainda.</p>}
          {history.map((r) => (
            <div key={r.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`redeem-${r.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-medium text-stone-900">{r.customer_name} · {r.items_description}</div>
                <div className="text-[11px] text-stone-500">{new Date(r.created_at).toLocaleString("pt-BR")} · compra R$ {r.items_value.toFixed(2)}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-lg font-bold text-stone-900">B$ {r.cashback_to_use.toFixed(2)}</div>
                {r.merchant_credit != null && <div className="text-[11px] text-emerald-700">a receber: B$ {r.merchant_credit.toFixed(2)}</div>}
              </div>
              {r.status === "pending" && <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-amber-100 text-amber-900">Aguardando cliente</span>}
              {r.status === "approved" && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800"><CheckCircle size={11}/> Aprovado</span>}
              {r.status === "rejected" && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-red-100 text-red-800"><XCircle size={11}/> Rejeitado</span>}
              {r.status === "cancelled" && <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-stone-200 text-stone-700">Cancelado</span>}
            </div>
          ))}
        </div>
      </Card>

      {scanOpen && <QrScanner onScan={onScan} onClose={() => setScanOpen(false)}/>}
    </div>
  );
}
